/**
 * Pure state logic for WeekView.
 *
 * WeekView coordinates optimistic saves, background reconciles, a per-week
 * cache and cross-week/cross-event staleness. None of that needs React: it is
 * map/array arithmetic over server truth, drafts and lock times. Extracting it
 * here makes the rules testable in isolation — the whole class of bugs this
 * file has produced (a reconcile undoing a confirmed save, a partial cache
 * entry read as a hit, a deletion that never registered as dirty) is a pure
 * function returning the wrong value, not a rendering problem.
 *
 * Every export is pure: no React, no fetch, no refs, no mutation of its
 * arguments. Where a function can return its input map unchanged it does so BY
 * REFERENCE, because these feed `setState` updaters and that identity is what
 * lets React bail out of a no-op render.
 *
 * Mirrored into apps/jest-client — keep the two copies in sync. The only
 * intended divergence is what the shared `@prisma/client` shim forces: the
 * jest-client's `PickSelection` has no DRAW.
 */

import type { PickSelection } from '@prisma/client';
import type {
  UndercardDetail,
  GamePickData,
  MyPropData,
  SurvivorPickInfo,
} from '@/services/events/main-event';

// ── Shared shapes ───────────────────────────────────────────────────────────

/**
 * The sides a SURVIVOR pick can hold. Deliberately narrower than
 * `PickSelection`: the schema carries DRAW for pick'em, but a survivor pick is
 * always a team, so DRAW is filtered out at the boundary (see
 * `resolveSavedPickForRound`).
 */
export type PickSide = 'HOME' | 'AWAY';

/** A survivor pick as the draft comparison sees it. */
export interface SurvivorSavedPick {
  gameId: string;
  side: PickSide;
}

/**
 * A survivor pick the server ACCEPTED this session, tagged with the round it
 * was made for. my-status is the usual saved truth; this covers the entered +
 * my-status-null case, where no optimistic status can safely be written.
 */
export interface ConfirmedWeekPick extends SurvivorSavedPick {
  undercardId: string;
}

/** The lock-relevant shape of a game — structural so tests need no full GameData. */
export interface LockableGame {
  id: string;
  /** Effective lock for this game: gameTime − offset in PER_GAME, else undercard.lockTime. */
  lockTime: Date | string;
}

/** The lock-relevant shape of a week. */
export interface LockContext {
  status: string;
  lockMode: string;
}

/** Per-week user data cached so switching back to a visited week is instant. */
export interface WeekCacheEntry {
  undercard: UndercardDetail;
  savedPicks: GamePickData[];
  draftPicks: Map<string, PickSelection>;
  /**
   * SURVIVOR's single-pick draft. It rides the same entry as draftPicks so both
   * formats survive a week switch identically — without it the sync effect
   * rebuilds weekPick from server truth on every switch and an unsaved survivor
   * pick is silently discarded.
   */
  weekPick: SurvivorSavedPick | null;
  myProps: MyPropData[];
  maxProps: number;
  purchasedCount: number;
  /**
   * The week's whole payload has landed. Writes stay keyed and unconditional
   * (that's what makes them safe from any week), but a load that returns early
   * on staleness leaves the entry holding an undercard and no picks — reading
   * THAT as a hit would only run a background refresh, which stops after the
   * undercard, so the picks would never arrive. Partial entries are misses.
   */
  complete: boolean;
}

// ── Staleness ───────────────────────────────────────────────────────────────

/**
 * The live id no longer matches the one this in-flight request captured — the
 * user has moved on, so any UN-KEYED write (setState) must be dropped. Keyed
 * writes (the week cache) stay unconditional: they land under the id they
 * belong to and are what a switch back reads.
 *
 * Used on both axes: the week guard (`undercardIdRef.current`) and the event
 * guard (`eventIdRef.current`), which stops a previous event's survivor
 * response from corrupting the new event's burned teams.
 */
export function isStaleContext(liveId: string, capturedId: string): boolean {
  return liveId !== capturedId;
}

// ── Locking ─────────────────────────────────────────────────────────────────

/**
 * Whether a single game's picks are locked right now. SCORING/COMPLETED
 * hard-lock the whole round in every mode; LOCKED hard-locks only in
 * WHOLE_UNDERCARD. Otherwise a game locks at its own effective lockTime
 * (gameTime − offset in PER_GAME).
 */
export function isGameLockedAt(
  game: Pick<LockableGame, 'lockTime'>,
  undercard: LockContext,
  nowMs: number,
): boolean {
  const hardLocked =
    undercard.status === 'SCORING' ||
    undercard.status === 'COMPLETED' ||
    (undercard.lockMode === 'WHOLE_UNDERCARD' && undercard.status === 'LOCKED');
  return hardLocked || new Date(game.lockTime).getTime() <= nowMs;
}

// ── Week cache ──────────────────────────────────────────────────────────────

/**
 * Merge a partial update into an existing cache entry.
 *
 * Returns null when there is nothing to build on (no existing entry and no
 * undercard in the update) — the caller drops the write rather than caching a
 * shell with no week in it.
 *
 * Two merge rules carry the weight here:
 *
 *  - `weekPick` merges on `undefined`, NOT on `??`. null is a MEANINGFUL value
 *    (the user clearing their survivor draft); with `??` a cleared draft would
 *    fall back to the cached one and get resurrected.
 *  - `complete` is never demoted. A background refresh writes a fresh undercard
 *    into a finished entry and must leave the flag alone; because partial
 *    writes simply omit the field, the existing value wins.
 */
export function mergeWeekCacheEntry(
  existing: WeekCacheEntry | undefined,
  update: Partial<WeekCacheEntry>,
): WeekCacheEntry | null {
  if (!existing && !update.undercard) return null;
  return {
    undercard: update.undercard ?? existing!.undercard,
    savedPicks: update.savedPicks ?? existing?.savedPicks ?? [],
    draftPicks: update.draftPicks ?? existing?.draftPicks ?? new Map(),
    weekPick: update.weekPick !== undefined ? update.weekPick : existing?.weekPick ?? null,
    myProps: update.myProps ?? existing?.myProps ?? [],
    maxProps: update.maxProps ?? existing?.maxProps ?? 5,
    purchasedCount: update.purchasedCount ?? existing?.purchasedCount ?? 0,
    complete: update.complete ?? existing?.complete ?? false,
  };
}

/**
 * Only a COMPLETE entry is a cache hit. A load that went stale mid-flight left
 * one holding an undercard and no picks; treating that as a hit would run the
 * background refresh, which returns right after the undercard, and the user's
 * saved picks would stay missing until a full remount.
 */
export function isCacheHit(entry: WeekCacheEntry | undefined): entry is WeekCacheEntry {
  return entry?.complete === true;
}

// ── Saved picks ─────────────────────────────────────────────────────────────

/** Index saved picks by gameId — the shape the dirty/picked computations read. */
export function savedPicksByGame(
  savedPicks: readonly GamePickData[],
): Map<string, PickSelection> {
  return new Map(savedPicks.map((p) => [p.gameId, p.pickedWinner] as const));
}

/** Fold accepted picks into a savedPicks list (scores stay server-owned). */
export function mergeSavedPicks(
  prev: readonly GamePickData[],
  accepted: readonly { gameId: string; pickedWinner: PickSelection }[],
): GamePickData[] {
  const next = [...prev];
  for (const p of accepted) {
    const i = next.findIndex((s) => s.gameId === p.gameId);
    if (i >= 0) next[i] = { ...next[i], pickedWinner: p.pickedWinner };
    else next.push({ gameId: p.gameId, pickedWinner: p.pickedWinner, isCorrect: null, pointsEarned: 0 });
  }
  return next;
}

/**
 * Correct the draft map against the reconciled saved rows after a save.
 *
 * Only the games this save SENT are touched, and only where the user hasn't
 * edited them since (`next.get(...) !== pickedWinner` → leave it alone). A game
 * the server no longer has is dropped from the draft; one it disagrees about
 * snaps to the server's value.
 *
 * Returns `draftPicks` BY REFERENCE when nothing changed, so the setState
 * updater bails out instead of re-rendering.
 */
export function reconcileDraftsAfterSave(
  draftPicks: Map<string, PickSelection>,
  sent: readonly { gameId: string; pickedWinner: PickSelection }[],
  merged: readonly GamePickData[],
): Map<string, PickSelection> {
  let changed = false;
  const next = new Map(draftPicks);
  for (const p of sent) {
    if (next.get(p.gameId) !== p.pickedWinner) continue; // user edited since — leave it
    const server = merged.find((f) => f.gameId === p.gameId)?.pickedWinner;
    if (server === undefined) {
      next.delete(p.gameId);
      changed = true;
    } else if (server !== p.pickedWinner) {
      next.set(p.gameId, server);
      changed = true;
    }
  }
  return changed ? next : draftPicks;
}

/**
 * Reconcile the games the server SKIPPED (they locked between pick and save)
 * back to the real saved state, so a locked card stops showing a pick that
 * never persisted. Always returns a new map — a skipped save is already a
 * state change worth committing.
 */
export function reconcileSkippedDrafts(
  draftPicks: ReadonlyMap<string, PickSelection>,
  skipped: readonly string[],
  savedPicks: readonly GamePickData[],
): Map<string, PickSelection> {
  const next = new Map(draftPicks);
  for (const id of skipped) {
    const s = savedPicks.find((p) => p.gameId === id);
    if (s) next.set(id, s.pickedWinner);
    else next.delete(id);
  }
  return next;
}

/**
 * When the live tick locks a game, snap its draft pick back to the saved truth
 * so a locked card never shows a selection that was never persisted (keeps the
 * action bar's unsaved state honest). Mirrors the skipped-games reconcile
 * above, driven by time rather than by a server response.
 *
 * Returns `draftPicks` BY REFERENCE when nothing changed — this runs on every
 * 1s tick, so the no-op path must not allocate a render.
 */
export function snapLockedDraftsToSaved(
  draftPicks: Map<string, PickSelection>,
  undercard: LockContext & { games: readonly LockableGame[] },
  savedPicks: readonly GamePickData[],
  nowMs: number,
): Map<string, PickSelection> {
  let changed = false;
  const next = new Map(draftPicks);
  for (const game of undercard.games) {
    if (!isGameLockedAt(game, undercard, nowMs)) continue;
    const saved = savedPicks.find((p) => p.gameId === game.id)?.pickedWinner;
    const cur = next.get(game.id);
    if (saved === undefined) {
      if (cur !== undefined) {
        next.delete(game.id);
        changed = true;
      }
    } else if (cur !== saved) {
      next.set(game.id, saved);
      changed = true;
    }
  }
  return changed ? next : draftPicks;
}

// ── Dirty state ─────────────────────────────────────────────────────────────

/**
 * Does the draft differ from the saved truth?
 *
 * Walked over the UNION of both key sets, not draftPicks alone: iterating the
 * draft made a REMOVED pick invisible, so deselecting a saved pick left the bar
 * showing "✓ All saved" with no way to push the change. Only still-open games
 * can be unsaved — a locked game's draft entry is gone but its saved row
 * remains, and that is not a pending edit.
 *
 * `undefined` on either side is meaningful: added (no saved), removed (no
 * draft), or changed (both, differing).
 */
export function hasUnsavedGamePicks(
  savedByGame: ReadonlyMap<string, PickSelection>,
  draftPicks: ReadonlyMap<string, PickSelection>,
  isOpenGame: (gameId: string) => boolean,
): boolean {
  return Array.from(new Set([...savedByGame.keys(), ...draftPicks.keys()])).some((gameId) => {
    if (!isOpenGame(gameId)) return false;
    return draftPicks.get(gameId) !== savedByGame.get(gameId);
  });
}

/** Survivor counterpart: one pick per week, so dirtiness is a straight compare. */
export function hasUnsavedSurvivorPick(
  draft: SurvivorSavedPick | null,
  saved: SurvivorSavedPick | null,
): boolean {
  return draft?.gameId !== saved?.gameId || draft?.side !== saved?.side;
}

// ── Effective picked set ────────────────────────────────────────────────────

/**
 * One source of truth for "this game has a pick" — feeds both the action-bar
 * meter and the carousel mini-rail's complete state, so the two can't drift.
 *
 * The EFFECTIVE selection, not a blind saved ∪ draft union: the draft owns an
 * open game (deselecting one has to drop the meter and un-gild the rail chip),
 * the saved row owns a locked one (its draft entry is gone, but the pick is
 * banked and still counts).
 */
export function effectivePickedGameIds(
  games: readonly { id: string }[],
  savedByGame: ReadonlyMap<string, PickSelection>,
  draftPicks: ReadonlyMap<string, PickSelection>,
  isOpenGame: (gameId: string) => boolean,
): ReadonlySet<string> {
  return new Set<string>(
    games
      .filter((g) => (isOpenGame(g.id) ? draftPicks.has(g.id) : savedByGame.has(g.id)))
      .map((g) => g.id),
  );
}

/** Survivor's picked set — the single week pick, if any. */
export function survivorPickedGameIds(weekPick: SurvivorSavedPick | null): ReadonlySet<string> {
  return new Set<string>(weekPick ? [weekPick.gameId] : []);
}

// ── Survivor pick resolution ────────────────────────────────────────────────

/**
 * The saved pick for a round, as the draft comparison sees it: the my-status
 * row when there is one, otherwise a pick the server accepted this session.
 *
 * The fallback is what covers "entered + my-status null" — that combination
 * gets no optimistic status write (writing a shell there wipes usedTeams), so
 * without it a 200 would still read as unsaved and strand the Save button. It
 * is keyed by undercardId on both paths, so a confirmation from another week
 * never leaks into this one.
 *
 * A DRAW row resolves to null: the schema's PickSelection carries DRAW for
 * pick'em, but a survivor pick is always a team. (In the jest-client copy the
 * enum has no DRAW, so this narrowing is a no-op there.)
 */
export function resolveSavedPickForRound(
  picksByWeek: readonly SurvivorPickInfo[] | undefined,
  confirmedWeekPick: ConfirmedWeekPick | null,
  roundUndercardId: string,
): SurvivorSavedPick | null {
  const info = picksByWeek?.find((p) => p.undercardId === roundUndercardId) ?? null;
  if (info) {
    return info.side === 'HOME' || info.side === 'AWAY'
      ? { gameId: info.gameId, side: info.side }
      : null;
  }
  return confirmedWeekPick && confirmedWeekPick.undercardId === roundUndercardId
    ? { gameId: confirmedWeekPick.gameId, side: confirmedWeekPick.side }
    : null;
}

/**
 * Reconcile the survivor draft with server truth on a week / my-status change.
 *
 * A live draft is only preserved when the server has no pick for this week AND
 * the draft belongs to this week's games, so a stale draft never survives a
 * week switch. A draft left behind on a week the user switched AWAY from isn't
 * lost by that: it lives in the week cache and the switch-back hydration puts
 * it back before this runs.
 */
export function resolveSurvivorDraft(
  prev: SurvivorSavedPick | null,
  savedPick: SurvivorSavedPick | null,
  undercard: { id: string; games: readonly { id: string }[] } | null,
  undercardId: string,
): SurvivorSavedPick | null {
  if (
    prev &&
    !savedPick &&
    undercard?.id === undercardId &&
    undercard.games.some((g) => g.id === prev.gameId)
  ) {
    return prev;
  }
  return savedPick;
}

/**
 * Survivor counterpart of `snapLockedDraftsToSaved`: a draft that differs from
 * the saved pick becomes invalid once either game locks (changing a pick
 * requires BOTH the old and new games to be open), so snap back to the saved
 * truth. Returns `prev` by reference when the draft already matches.
 */
export function snapLockedWeekPick(
  prev: SurvivorSavedPick | null,
  savedPick: SurvivorSavedPick | null,
  undercard: LockContext & { games: readonly LockableGame[] },
  nowMs: number,
): SurvivorSavedPick | null {
  if (prev?.gameId === savedPick?.gameId && prev?.side === savedPick?.side) return prev;
  const draftGame = prev ? undercard.games.find((g) => g.id === prev.gameId) : undefined;
  const savedGame = savedPick ? undercard.games.find((g) => g.id === savedPick.gameId) : undefined;
  const draftLocked = !!draftGame && isGameLockedAt(draftGame, undercard, nowMs);
  const savedLocked = !!savedGame && isGameLockedAt(savedGame, undercard, nowMs);
  return draftLocked || savedLocked ? savedPick : prev;
}
